import type { BuildStreamStartDecision, SnapshotIndexEntry } from './shouldStartBuildStream';
import { isLeasedElsewhere, type TurnVersion } from './turnVersions';

/**
 * Whether a turn's pill should say "Didn't finish" (test finding 2: an interrupted build showed a
 * normal pill, and clicking it only said "This turn hasn't built a prototype yet").
 *
 * Only the server's version decides. The message's own `buildStatus` is not a fallback: it is
 * whatever the last stream wrote to the build session, and a turn finished later (by a resume, or
 * a server-side build) can keep `error` or `running` there. Read on its own, that showed a verified
 * build as unfinished until the panel opened and the versions loaded (seen on test, V7).
 * Without a version the answer is false, the same pill as before this change.
 */
export function turnBuildDidNotFinish(args: {
  message: { buildStatus?: string };
  version?: TurnVersion;
  indexEntry?: Pick<SnapshotIndexEntry, 'buildVerified'>;
  ownerTabId: string;
  now?: number;
}): boolean {
  const { message, version, indexEntry, ownerTabId, now } = args;
  if (!version) return false;
  if (indexEntry?.buildVerified === true) return false;
  // This tab saw the build pass. The version list is fetched, not pushed, so it can still say
  // `draft` for a few seconds after that; without this the pill flashed "Didn't finish".
  if (message.buildStatus === 'success') return false;
  // Another tab is building it right now; that tab shows the progress.
  if (version.status === 'draft') return !isLeasedElsewhere(version, ownerTabId, now);
  return version.status === 'failed';
}

/**
 * What clicking an unfinished turn's pill does, from `shouldStartBuildStream`'s decision for that
 * turn. Resuming is free (no model call), so it runs on the click. Building again spends credits,
 * so it goes through Retry, which only exists for the latest plan turn; an older turn cannot be
 * rebuilt in place without rewriting the versions after it.
 */
export type ContinueTurnAction =
  | 'resume-build'
  | 'resume-fix'
  | 'resume-from-files'
  | 'retry'
  | 'ask-again'
  | 'wait'
  | 'restore';

export function continueUnfinishedTurnAction(
  decision: BuildStreamStartDecision,
  isLatestPlanTurn: boolean
): ContinueTurnAction {
  switch (decision) {
    case 'resume-build':
    case 'resume-fix':
    case 'resume-from-files':
    case 'wait':
      return decision;
    // The server says the turn is done after all (another tab finished it): show it.
    case 'skip':
      return 'restore';
    case 'unfinished':
    case 'start':
      return isLatestPlanTurn ? 'retry' : 'ask-again';
  }
}
